import { useEffect, useRef, useState } from "react";
import { Flex, Spin } from "antd";
import { PrinterOutlined, LeftOutlined } from "@ant-design/icons";
import Button from "@/common/elements/Button";
import { StyledDarkSectionTitle } from "@/common/styles";
import { fetchBookingDetail } from "./moduleSearchApi";
import {
  ReportSectionList,
  ReportBioHeader,
  useJacketReportSections,
  mapDistinguishingCharacteristics,
  buildJacketReportPrintHtml,
} from "./JacketReportSections";
import {
  SummaryWrapper,
  SummaryHeaderBar,
  ReportSectionsWrapper,
} from "./styles";

// Read-only summary shown when Super Search's Module = Bookings and a
// result row is clicked. Renders the *same* Jacket Summary Report body as
// JacketFullSummaryReport (bio header + all SECTION_DEFS tables) — matches
// ASP, which opens the one Jacket Summary Report (reports/JacketSummaryNew
// .asp) regardless of which module's Details/Reports button you clicked
// (see InvestigatorsViewInterface.asp's ListButtonOverride_Details, always
// fieldSourceToGet = "Jackets"). The header bar still names the specific
// booking you clicked into, since that's real context Super Search itself
// doesn't otherwise show once you're on this screen; the booking itself
// still shows up as a row in the Arrests section below like any other.
//
// jacketId comes from the search-result row immediately (BOOKING_HEADERS
// includes it, so the section fetch can start right away) and is
// reconciled against the full booking detail's own jacketId once that
// call resolves, in case the two ever disagree.
const BookingSummaryReport = ({ row, onBack, onClose }) => {
  const printRef = useRef();
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!row?.bookingNumber) {
      setLoadingDetail(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingDetail(true);
    fetchBookingDetail(row.bookingNumber)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        // fall back to the search row's own fields below
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [row?.bookingNumber]);

  const bookingNumber = detail?.bookingNumber || row?.bookingNumber;
  const jacketId = detail?.jacketId || row?.jacketId;
  const bio = detail?.bio?.[0] || {};

  const { loading: loadingSections, sectionRows: fetchedSectionRows } =
    useJacketReportSections(jacketId);
  const loading = loadingDetail || loadingSections;
  const sectionRows = {
    ...fetchedSectionRows,
    distinguishingCharacteristics: mapDistinguishingCharacteristics(
      bio?.distinguishingCharacteristics,
    ),
  };

  const handlePrint = () => {
    const reportContent = printRef.current;
    if (!reportContent) return;

    const printWindow = window.open("", "", "width=900,height=1000");
    printWindow.document.write(
      buildJacketReportPrintHtml(
        `Booking Summary — Booking ${bookingNumber}`,
        reportContent.innerHTML,
      ),
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <SummaryWrapper>
      <SummaryHeaderBar justify="space-between" align="center">
        <Flex align="center" gap="small">
          <Button icon={<LeftOutlined />} onClick={onBack}>
            Back to Results
          </Button>
          <StyledDarkSectionTitle as="span">
            Booking Summary — {bookingNumber}
          </StyledDarkSectionTitle>
        </Flex>
        <Flex gap="small">
          <Button
            color="primary"
            type="primary"
            variant="solid"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      </SummaryHeaderBar>

      <Spin spinning={loading}>
        <div ref={printRef} className="report-content">
          <ReportSectionsWrapper>
            <ReportBioHeader bio={bio} jacketId={jacketId} />

            <ReportSectionList sectionRows={sectionRows} />
          </ReportSectionsWrapper>
        </div>
      </Spin>
    </SummaryWrapper>
  );
};

export default BookingSummaryReport;
